import {Action, CombinedState, ThunkAction, ThunkDispatch} from "@reduxjs/toolkit";

import { Types, Services, Enums } from "@sendum/core";

import { TFilter } from "../types/TFilter";

import { ShipmentSelectorRootState, DispatchAction } from "./ShipmentSelectorStore";
import { IShipmentSelectorState } from "./ShipmentSelectorReducer";
import { AnyAction } from "redux";
import { toast } from "react-hot-toast";
import { AccountRole } from "@sendum/core/src/types";

export enum ShipmentSelectorActionTypes {
	SET_COUNTS_FILTER,
	SET_SEARCH,
	SET_FILTER,
	SET_LIMIT,
	SET_QUERY,
	SET_LOADING,
	END_INITIAL_LOADING,
	SET_FILTERING,
	SET_TOTAL,
	SET_ERROR,
	RESET_STATE,
	CLEAR_SHIPMENTS,
	ADD_SHIPMENTS,
	SET_SORT_DIRECTION,
	SET_READONLY,
}

export const resetState = (): DispatchAction => ({
	type: ShipmentSelectorActionTypes.RESET_STATE,
	payload: {},
});

export const setSearch = (search: string): DispatchAction => ({
	type: ShipmentSelectorActionTypes.SET_SEARCH,
	payload: {
		search,
	},
});

export const setFilter = (filter: TFilter): DispatchAction => ({
	type: ShipmentSelectorActionTypes.SET_FILTER,
	payload: {
		filter,
	},
});

export const setLimit = (limit: number): DispatchAction => ({
	type: ShipmentSelectorActionTypes.SET_LIMIT,
	payload: {
		limit,
	},
});

export const setQuery = (query: Partial<Types.TLoadShipmentsQuery>): DispatchAction => ({
	type: ShipmentSelectorActionTypes.SET_QUERY,
	payload: {
		query,
	},
});

export const setLoading = (isLoading: boolean): DispatchAction => ({
	type: ShipmentSelectorActionTypes.SET_LOADING,
	payload: {
		isLoading,
	},
});

export const endInitialLoading = (): DispatchAction => ({
	type: ShipmentSelectorActionTypes.END_INITIAL_LOADING,
	payload: {},
});

export const clearShipments = (): DispatchAction => ({
	type: ShipmentSelectorActionTypes.CLEAR_SHIPMENTS,
	payload: {},
});

export const addShipments = (shipments: Types.TSmallShipment[]): DispatchAction => ({
	type: ShipmentSelectorActionTypes.ADD_SHIPMENTS,
	payload: {
		shipments,
	},
});

export const setFiltering = (isFiltering: boolean): DispatchAction => ({
	type: ShipmentSelectorActionTypes.SET_FILTERING,
	payload: {
		isFiltering,
	},
});

export const setTotal = (total: number): DispatchAction => ({
	type: ShipmentSelectorActionTypes.SET_TOTAL,
	payload: {
		total,
	},
});

export const setError = (error: Error): DispatchAction => ({
	type: ShipmentSelectorActionTypes.SET_ERROR,
	payload: {
		error,
	},
});

export const setReadOnly = (isReadOnly: boolean): DispatchAction => ({
	type: ShipmentSelectorActionTypes.SET_READONLY,
	payload: {
		isReadOnly,
	},
});

const clearShipment = async (dispatch: ThunkDispatch<CombinedState<{

}>, null, Action<string>>): Promise<void> => {
	await dispatch(clearShipments());
	await dispatch(addShipments([]));
	await dispatch(setTotal(0));
	await dispatch(endInitialLoading());
	await dispatch(setLoading(false));
}

export const loadShipments = (clear = false): ThunkAction<void, ShipmentSelectorRootState, null, Action<string>> => async (dispatch, getState) => {
	const state: IShipmentSelectorState = getState().ShipmentSelector;
	if (state.isLoading) {
		return;
	}


	await dispatch(setLoading(true) as unknown as AnyAction);
	const query: Partial<Types.TLoadShipmentsQuery> = {
		view: "list",
		limit: 1000,
	};
	state.filter.list.forEach(f => {
		if (f.name) {
			query[f.name] = f.value;
			query[`${f.name}Operator`] = f.operator ?? "eq";
		}
	});
	if (state.filter.sortBy) {
		query.sortBy = state.filter.sortBy;
		query.order = state.filter.sortOrder === Enums.SortDirection.ASC ? "asc" : "desc";
	}
	try {
		const response = await Services.API.WidgetsApi.getShipmentsList(query);

		if (clear) {
			await dispatch(clearShipments());
		}
		await dispatch(addShipments(response.shipments));
		await dispatch(setTotal(response.total));
		await dispatch(endInitialLoading());
		await dispatch(setLoading(false));
	} catch (error) {
		toast.error((error as Error).message);
		await clearShipment(dispatch);
	}
};


export const loadShipmentsRO = (clear = false): ThunkAction<void, ShipmentSelectorRootState, null, Action<string>> => async (dispatch, getState) => {
	const state: IShipmentSelectorState = getState().ShipmentSelector;
	if (state.isLoading || state.isReadOnly) {
		return;
	}


	await dispatch(setLoading(true) as unknown as AnyAction);
	if(state.isReadOnly === undefined) {
		try {
			const response = await Services.API.WidgetsApi.getAccount();
			if (response.Role === AccountRole.VIEW_ONLY_USER) {
				await dispatch(setReadOnly(true));
				await clearShipment(dispatch);
				return;
			} else {
				await dispatch(setReadOnly(false));
			}
		} catch (error) {
			toast.error((error as Error).message);
			await clearShipment(dispatch);
			return;
		}
	}

	const query: Partial<Types.TLoadShipmentsQuery> = {
		view: "list",
		limit: 1000,
	};
	state.filter.list.forEach(f => {
		if (f.name) {
			query[f.name] = f.value;
			query[`${f.name}Operator`] = f.operator ?? "eq";
		}
	});
	if (state.filter.sortBy) {
		query.sortBy = state.filter.sortBy;
		query.order = state.filter.sortOrder === Enums.SortDirection.ASC ? "asc" : "desc";
	}
	try {
		const response = await Services.API.WidgetsApi.getShipmentsList(query);

		if (clear) {
			await dispatch(clearShipments());
		}
		await dispatch(addShipments(response.shipments));
		await dispatch(setTotal(response.total));
		await dispatch(endInitialLoading());
		await dispatch(setLoading(false));
	} catch (error) {
		toast.error((error as Error).message);
		await clearShipment(dispatch);
	}
};
